import { Link } from "react-router-dom";
import { ArrowRight, BedDouble, CalendarRange, CheckCircle2, Plane, Users, Vote, Wallet } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";

const HERO = "https://images.unsplash.com/photo-1573269354259-8c108692afa1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const FEATURES = [
  [Wallet, "Everyone's real total", "Shared costs, optional activities, rooms and credits roll into one clear, itemized total per person.", "bg-orange-100 text-orange-600"],
  [CalendarRange, "A plan people can follow", "Day-by-day itinerary with optional sign-ups, reservations and change alerts.", "bg-teal-100 text-teal-700"],
  [Users, "Invites that just work", "Share a link or email invites, track RSVPs and give each traveler a personal portal.", "bg-violet-100 text-violet-700"],
  [BedDouble, "Rooms without drama", "Capacities, roommate requests and assignments with conflict warnings.", "bg-amber-100 text-amber-700"],
  [Vote, "Decide together", "Polls for restaurants, dates, budgets and themes — with deadlines.", "bg-rose-100 text-rose-600"],
  [Plane, "Who lands when", "An arrivals board so pickups and check-ins are easy to coordinate.", "bg-sky-100 text-sky-700"],
];
const OCCASIONS = ["Birthday trips", "Girls' trips", "Family reunions", "Destination weddings", "Retreats", "Friends' getaways"];

export default function Landing() {
  return (
    <PublicLayout>
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:px-8 lg:pt-16">
          <div className="rise">
            <span className="pill bg-teal-100 text-teal-800">Free for unlimited trips</span>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Group trips, <span className="text-orange-500">minus</span> the group-chat chaos.
            </h1>
            <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
              Triply keeps your people, money, plans and rooms in one friendly place — so everyone knows what they owe, where to be, and who's bunking with whom.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-semibold text-white shadow-lg shadow-orange-500/25 transition-transform hover:-translate-y-0.5 hover:bg-orange-600" data-testid="hero-cta-signup">
                Plan a trip free <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing" className="rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-800 hover:bg-slate-50" data-testid="hero-cta-pricing">See plans</Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {OCCASIONS.map((o) => <span key={o} className="pill bg-white text-slate-600 ring-1 ring-slate-200">{o}</span>)}
            </div>
          </div>
          <div className="relative rise" style={{ animationDelay: ".12s" }}>
            <img src={HERO} alt="Friends on a beach trip at sunset" className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-2xl" />
            <div className="card absolute -bottom-6 left-4 w-64 p-4 sm:left-[-2rem]" aria-hidden="true">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Example breakdown</p>
              <p className="mt-1 font-display text-lg font-bold">Priya · Lisbon</p>
              <div className="mt-2 space-y-1 font-mono text-sm">
                <div className="flex justify-between"><span>Total</span><span>€1,013</span></div>
                <div className="flex justify-between text-teal-700"><span>Recorded paid</span><span>€300</span></div>
                <div className="flex justify-between font-semibold text-orange-600"><span>Remaining</span><span>€713</span></div>
              </div>
              <p className="mt-2 text-xs text-slate-500">Next: €200 due May 1</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="max-w-2xl text-2xl font-bold sm:text-3xl">Everything a group trip needs. Nothing it doesn't.</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(([Icon, t, d, c], i) => (
            <div key={t} className="card card-hover rise p-6" style={{ animationDelay: `${i * 0.05}s` }}>
              <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${c}`}><Icon className="h-5 w-5" /></span>
              <h3 className="mt-4 text-lg font-semibold">{t}</h3>
              <p className="mt-2 text-sm text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 rounded-[2rem] bg-slate-900 p-8 text-white sm:p-12 lg:grid-cols-2">
          <div>
            <h2 className="text-2xl font-bold sm:text-3xl">Honest money tracking</h2>
            <p className="mt-3 text-slate-300">Collect money however your group already does — bank transfer, cash, payment apps. Triply records it, explains every total, and tracks deposits and installments. It never pretends to have verified a transfer it didn't process.</p>
          </div>
          <ul className="space-y-3">
            {["Deposits, installments and due dates", "Partial payments, overpayments, credits and refunds", "Optional activities update only the people who opt in", "A full audit history of every change"].map((x) => (
              <li key={x} className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-400" />{x}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-2xl font-bold sm:text-3xl">Your next trip starts with one invite.</h2>
            <p className="mt-2 text-slate-600">Free forever for unlimited trips with up to 10 travelers each.</p>
          </div>
          <Link to="/register" className="rounded-full bg-teal-600 px-6 py-3 font-semibold text-white hover:bg-teal-700" data-testid="bottom-cta-signup">Create your free account</Link>
        </div>
      </section>
    </PublicLayout>
  );
}
